import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Form, Button } from "semantic-ui-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const PostModal = ({
  open,
  onClose,
  onSubmit,
  formData,
  onChange,
  loading = false,
  currentUser,
}) => {
  // Local states for debounced fields
  const [localTitle, setLocalTitle] = useState(formData.title);
  const [localDescription, setLocalDescription] = useState(
    formData.description
  );
  const [localTargetGroup, setLocalTargetGroup] = useState(
    Array.isArray(formData.targetGroup)
      ? formData.targetGroup
      : [formData.targetGroup || "all"]
  );

  // Ref for ReactQuill
  const quillRef = useRef(null);

  // Debounce refs
  const debounceTitleRef = useRef(null);
  const debounceDescRef = useRef(null);
  const debounceTargetGroupRef = useRef(null);

  // Track if we're updating from user interaction
  const isUserInteractionRef = useRef(false);
  const prevOpenRef = useRef(open);

  // Sync props with local state only when modal opens or closes
  useEffect(() => {
    // Only sync when modal opens or if it's not a user interaction
    if (open !== prevOpenRef.current) {
      setLocalTitle(formData.title);
      setLocalDescription(formData.description);
      setLocalTargetGroup(
        Array.isArray(formData.targetGroup)
          ? formData.targetGroup
          : [formData.targetGroup || "all"]
      );
      prevOpenRef.current = open;
    }
  }, [open, formData.title, formData.description, formData.targetGroup]);

  // Fix ReactQuill focus issues in modal and setup proper event listeners
  useEffect(() => {
    if (open && quillRef.current) {
      // Small delay to ensure modal is fully rendered
      const timer = setTimeout(() => {
        const quill = quillRef.current.getEditor();
        if (quill) {
          // Force focus and ensure toolbar works properly
          quill.focus();

          // Add custom toolbar event listeners for better toggle behavior
          const toolbar = quill.getModule("toolbar");
          if (toolbar) {
            const toolbarContainer = toolbar.container;

            // Override toolbar button behavior for better toggling
            const buttons = toolbarContainer.querySelectorAll("button");
            buttons.forEach((button) => {
              button.addEventListener("mousedown", (e) => {
                // Prevent default to avoid focus loss
                e.preventDefault();

                // Get current selection
                const range = quill.getSelection();
                if (range) {
                  // Store selection
                  const index = range.index;
                  const length = range.length;

                  // Allow the click to proceed
                  setTimeout(() => {
                    // Restore selection after toolbar action
                    quill.setSelection(index, length);
                  }, 0);
                }
              });
            });
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Debounce title
  useEffect(() => {
    clearTimeout(debounceTitleRef.current);
    debounceTitleRef.current = setTimeout(() => {
      if (localTitle !== formData.title) {
        onChange("title", localTitle);
      }
    }, 400);
    return () => clearTimeout(debounceTitleRef.current);
  }, [localTitle]);

  // Debounce description
  useEffect(() => {
    clearTimeout(debounceDescRef.current);
    debounceDescRef.current = setTimeout(() => {
      if (localDescription !== formData.description) {
        onChange("description", localDescription);
      }
    }, 500);
    return () => clearTimeout(debounceDescRef.current);
  }, [localDescription]);

  // Debounce target group with proper array comparison
  useEffect(() => {
    clearTimeout(debounceTargetGroupRef.current);
    debounceTargetGroupRef.current = setTimeout(() => {
      // Only update if localTargetGroup has values
      if (localTargetGroup && localTargetGroup.length > 0) {
        onChange("targetGroup", localTargetGroup);
      }
    }, 400);
    return () => clearTimeout(debounceTargetGroupRef.current);
  }, [localTargetGroup]);

  // Permission check
  const canPost =
    currentUser &&
    (Array.isArray(currentUser.roles)
      ? currentUser.roles.some((r) =>
          ["admin", "librarian"].includes(r.toLowerCase())
        )
      : ["admin", "librarian"].includes(currentUser?.roles?.toLowerCase?.()));

  if (!canPost) return null;

  const targetGroupOptions = [
    { key: "all", text: "All Users", value: "all" },
    { key: "student", text: "Student", value: "student" },
    { key: "faculty", text: "Faculty", value: "faculty" },
    { key: "librarian", text: "Librarian", value: "librarian" },
    { key: "visitor", text: "Visitor", value: "visitor" },
    { key: "researcher", text: "Researcher", value: "researcher" },
  ];

  // Enhanced ReactQuill modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ["link"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "color",
    "background",
    "align",
    "link",
  ];

  // Handle ReactQuill change with proper event handling
  const handleQuillChange = (content, delta, source, editor) => {
    setLocalDescription(content);
  };

  // Handle toolbar button clicks to ensure proper toggling
  const handleQuillFocus = () => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      // Ensure toolbar buttons reflect current selection
      setTimeout(() => {
        quill.focus();
      }, 0);
    }
  };

  // Handle selection change to update toolbar state
  const handleSelectionChange = (range, source, editor) => {
    if (range && quillRef.current) {
      const quill = quillRef.current.getEditor();
      // Force toolbar to update its state
      setTimeout(() => {
        const toolbar = quill.getModule("toolbar");
        if (toolbar) {
          toolbar.update(range);
        }
      }, 0);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{ position: "relative", zIndex: 1000 }}
    >
      <Modal.Header style={{ color: "#000000" }}>
        Post Library Announcement
      </Modal.Header>
      <Modal.Content style={{ color: "#000000" }}>
        <Form>
          <Form.Input
            label="Title"
            value={localTitle}
            onChange={(e, { value }) => setLocalTitle(value)}
            required
            style={{ color: "#000000" }}
          />
          <Form.Field required>
            <label style={{ color: "#000000" }}>Description</label>
            <div style={{ position: "relative", zIndex: 1001 }}>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={localDescription}
                onChange={handleQuillChange}
                onFocus={handleQuillFocus}
                onSelectionChange={handleSelectionChange}
                modules={modules}
                formats={formats}
                style={{
                  height: "150px",
                  marginBottom: window.innerWidth <= 767 ? "5em" : "4em",
                  backgroundColor: "white",
                  color: "#000000",
                }}
                preserveWhitespace
                bounds=".ui.modal"
              />
            </div>
          </Form.Field>
          <Form.Select
            label="Target Groups"
            placeholder="Select target audience(s)"
            options={targetGroupOptions}
            value={localTargetGroup}
            onChange={(e, { value }) => {
              isUserInteractionRef.current = true;

              // If "all" is selected, clear other selections
              if (value.includes("all") && !localTargetGroup.includes("all")) {
                setLocalTargetGroup(["all"]);
              }
              // If other options are selected and "all" was previously selected, remove "all"
              else if (value.length > 1 && localTargetGroup.includes("all")) {
                setLocalTargetGroup(value.filter((v) => v !== "all"));
              }
              // Regular multi-selection
              else {
                setLocalTargetGroup(value);
              }

              setTimeout(() => {
                isUserInteractionRef.current = false;
              }, 500);
            }}
            multiple
            required
            style={{ color: "#000000" }}
          />
          <Form.Field>
            <label style={{ color: "#000000" }}>
              Image or Video (optional)
            </label>

            {/* Show existing image if editing */}
            {!formData.image && (formData.imageUrl || formData.image_url) && (
              <div style={{ marginBottom: "1em" }}>
                <p
                  style={{
                    fontSize: "0.9em",
                    color: "#666",
                    marginBottom: "0.5em",
                  }}
                >
                  Current image:
                </p>
                <div style={{ position: "relative", display: "inline-block" }}>
                  <img
                    src={formData.imageUrl || formData.image_url}
                    alt="Current"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "200px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      border: "1px solid #ddd",
                    }}
                  />
                  <Button
                    icon="trash"
                    color="red"
                    size="tiny"
                    circular
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                    }}
                    onClick={() => {
                      onChange("imageUrl", null);
                      onChange("image_url", null);
                      onChange("removeImage", true);
                    }}
                    title="Remove image"
                  />
                </div>
                <p
                  style={{
                    fontSize: "0.85em",
                    color: "#888",
                    marginTop: "0.5em",
                  }}
                >
                  Upload a new file to replace this image, or click the trash
                  icon to remove it
                </p>
              </div>
            )}

            <Form.Input
              type="file"
              accept="image/*, video/*"
              onChange={(e) => onChange("image", e.target.files[0] || null)}
              style={{ color: "#000000" }}
            />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="green"
          onClick={onSubmit}
          loading={loading}
          disabled={
            !formData.title ||
            !formData.description ||
            !formData.targetGroup ||
            (Array.isArray(formData.targetGroup) &&
              formData.targetGroup.length === 0) ||
            loading
          }
        >
          Post Announcement
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

PostModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    targetGroup: PropTypes.string.isRequired,
    image: PropTypes.any,
    imageUrl: PropTypes.string,
    image_url: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  currentUser: PropTypes.shape({
    roles: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }),
};

export default PostModal;
